import { useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, TouchEvent as ReactTouchEvent, WheelEvent as ReactWheelEvent } from "react";
import type { Ride } from "../types/ride";
import {
  type RideAnalysisChartModel,
  buildRideAnalysisChartModel,
  clampViewport,
  computeViewportForInterval,
  createRideInteractionKey,
  findIntervalIdAtElapsed,
  findNearestPointIndex,
  getSafePlotDimension
} from "../utils/rideAnalysisChartSelectors";
import type { RespiratoryChartPoint } from "../utils/rideAnalysisChartSelectors";

type RideAnalysisChartProps = {
  ride: Ride | null;
  ftpWatts: number | null;
  selectedIntervalId: string | null;
  onSelectedIntervalChange: (intervalId: string | null) => void;
  isParsingRide: boolean;
  rideParseError: string | null;
  respiratoryPoints?: RespiratoryChartPoint[];
};

type Viewport = {
  startSeconds: number;
  endSeconds: number;
};

const CHART_HEIGHT = 120;
const CHART_PADDING_TOP = 12;
const CHART_PADDING_BOTTOM = 24;
const CHART_PADDING_LEFT = 52;
const CHART_PADDING_RIGHT = 20;
const MAX_RENDER_POINTS = 1200;
const MIN_WINDOW_SECONDS = 20;

type ChartSeriesKey = "powerWatts" | "power30sWatts" | "heartRateBpm" | "cadenceRpm" | "elevationFeet" | "minuteVentilation" | "breathingFrequency";

type TouchPinchState = {
  initialDistancePx: number;
  initialViewport: Viewport;
};

type RideAnalysisChartViewState = {
  records: Ride["records"];
  detectedIntervals: NonNullable<Ride["rideAnalytics"]>["intervalDetection"]["detectedIntervals"];
  hasRideData: boolean;
  chartModel: RideAnalysisChartModel;
  effectiveSelectedIntervalId: string | null;
  safePlotWidth: number;
  activeViewport: Viewport;
  durationSeconds: number;
  safeDuration: number;
  xTicks: number[];
};

export function getFullRideViewport(chartModel: RideAnalysisChartModel): Viewport {
  return {
    startSeconds: chartModel.domainStartSeconds,
    endSeconds: chartModel.domainEndSeconds
  };
}

function toFiniteNumber(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatElapsedTime(seconds: number): string {
  const clamped = Math.max(0, Math.round(seconds));
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const secs = clamped % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

function formatClockTime(isoTimestamp: string | null): string {
  if (!isoTimestamp) {
    return "--";
  }

  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

function formatMetric(value: number | null, unit: string, decimals = 0): string {
  if (value === null) {
    return "--";
  }

  return `${value.toFixed(decimals)} ${unit}`;
}

function formatClassification(classification: string | null): string {
  if (!classification) {
    return "--";
  }

  return classification
    .split("-")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getTouchDistance(touches: ArrayLike<{ clientX: number; clientY: number }>): number | null {
  if (touches.length < 2) {
    return null;
  }

  const first = touches[0];
  const second = touches[1];
  const dx = first.clientX - second.clientX;
  const dy = first.clientY - second.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function downsampleIndices(elapsed: number[], values: Array<number | null>, maxPoints: number): number[] {
  if (elapsed.length <= maxPoints) {
    return elapsed.map((_, index) => index);
  }

  const bucketSize = Math.max(1, Math.floor(elapsed.length / maxPoints));
  const selected = new Set<number>([0, elapsed.length - 1]);

  for (let bucketStart = 0; bucketStart < elapsed.length; bucketStart += bucketSize) {
    const bucketEnd = Math.min(elapsed.length, bucketStart + bucketSize);
    let minIndex = -1;
    let maxIndex = -1;
    let minValue = Number.POSITIVE_INFINITY;
    let maxValue = Number.NEGATIVE_INFINITY;

    for (let index = bucketStart; index < bucketEnd; index += 1) {
      const value = values[index];
      if (value === null) {
        continue;
      }

      if (value < minValue) {
        minValue = value;
        minIndex = index;
      }

      if (value > maxValue) {
        maxValue = value;
        maxIndex = index;
      }
    }

    if (minIndex >= 0) {
      selected.add(minIndex);
    }

    if (maxIndex >= 0) {
      selected.add(maxIndex);
    }
  }

  return [...selected].sort((left, right) => left - right);
}

function buildLinePath(
  xValues: number[],
  yValues: Array<number | null>,
  xScale: (value: number) => number,
  yScale: (value: number) => number,
  maxPoints: number
): string {
  const indices = downsampleIndices(xValues, yValues, maxPoints);
  let path = "";
  let isSegmentOpen = false;

  for (const index of indices) {
    const value = yValues[index];
    if (value === null) {
      isSegmentOpen = false;
      continue;
    }

    const x = xScale(xValues[index]);
    const y = yScale(value);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      isSegmentOpen = false;
      continue;
    }

    if (!isSegmentOpen) {
      path += `M ${x.toFixed(2)} ${y.toFixed(2)} `;
      isSegmentOpen = true;
    } else {
      path += `L ${x.toFixed(2)} ${y.toFixed(2)} `;
    }
  }

  return path.trim();
}

function computeValueRange(values: Array<number | null>): { min: number; max: number } | null {
  const finiteValues = values.filter((value): value is number => value !== null && Number.isFinite(value));
  if (finiteValues.length === 0) {
    return null;
  }

  let min = Math.min(...finiteValues);
  let max = Math.max(...finiteValues);

  if (min === max) {
    const padding = Math.max(1, Math.abs(min * 0.03));
    min -= padding;
    max += padding;
  }

  return { min, max };
}

function clampView(domainStart: number, domainEnd: number, viewport: Viewport): Viewport {
  return clampViewport(domainStart, domainEnd, viewport.startSeconds, viewport.endSeconds, MIN_WINDOW_SECONDS);
}

export function hasInvalidViewport(viewport: Viewport | null, domainStart: number, domainEnd: number): boolean {
  if (!viewport) {
    return true;
  }

  if (!Number.isFinite(viewport.startSeconds) || !Number.isFinite(viewport.endSeconds)) {
    return true;
  }

  if (viewport.endSeconds <= viewport.startSeconds) {
    return true;
  }

  if (viewport.startSeconds < domainStart || viewport.endSeconds > domainEnd) {
    return true;
  }

  return false;
}

function createEmptyChartModel(ftpWatts: number | null): RideAnalysisChartModel {
  return {
    points: [],
    domainStartSeconds: 0,
    domainEndSeconds: 1,
    hasPower: false,
    hasHeartRate: false,
    hasCadence: false,
    hasElevation: false,
    hasGps: false,
    hasMinuteVentilation: false,
    hasBreathingFrequency: false,
    ftpWatts: toFiniteNumber(ftpWatts),
    overlays: [],
    overlaysByIntervalId: {}
  };
}

export function buildRideAnalysisChartViewState({
  ride,
  selectedIntervalId,
  plotWidth,
  viewport,
  chartModel
}: {
  ride: Ride | null;
  selectedIntervalId: string | null;
  plotWidth: number;
  viewport: Viewport | null;
  chartModel: RideAnalysisChartModel;
}): RideAnalysisChartViewState {
  const records = ride?.records ?? [];
  const detectedIntervals = ride?.rideAnalytics?.intervalDetection?.detectedIntervals ?? [];
  const hasRideData = records.length > 0;
  const effectiveSelectedIntervalId =
    selectedIntervalId && chartModel.overlaysByIntervalId[selectedIntervalId] ? selectedIntervalId : null;
  const safePlotWidth = getSafePlotDimension(plotWidth);
  const fullViewport = getFullRideViewport(chartModel);
  const activeViewport = hasRideData
    ? hasInvalidViewport(viewport, chartModel.domainStartSeconds, chartModel.domainEndSeconds)
      ? fullViewport
      : clampView(chartModel.domainStartSeconds, chartModel.domainEndSeconds, viewport as Viewport)
    : { startSeconds: 0, endSeconds: 0 };
  const durationSeconds = hasRideData ? Math.max(0, activeViewport.endSeconds - activeViewport.startSeconds) : 0;
  const safeDuration = Math.max(1, durationSeconds);
  const targetTickSpacingPx = 120;
  const steps = Math.max(2, Math.min(8, Math.floor(safePlotWidth / targetTickSpacingPx)));
  const xTicks = new Array(steps + 1)
    .fill(0)
    .map((_, index) => activeViewport.startSeconds + (durationSeconds * index) / Math.max(1, steps));

  return {
    records,
    detectedIntervals,
    hasRideData,
    chartModel,
    effectiveSelectedIntervalId,
    safePlotWidth,
    activeViewport,
    durationSeconds,
    safeDuration,
    xTicks
  };
}

export function RideAnalysisChart({
  ride,
  ftpWatts,
  selectedIntervalId,
  onSelectedIntervalChange,
  isParsingRide,
  rideParseError,
  respiratoryPoints = []
}: RideAnalysisChartProps) {
  const [showSmoothedPower, setShowSmoothedPower] = useState(true);
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const [hoveredElapsedSeconds, setHoveredElapsedSeconds] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragStartViewport, setDragStartViewport] = useState<Viewport | null>(null);
  const [touchPinch, setTouchPinch] = useState<TouchPinchState | null>(null);
  const [plotWidth, setPlotWidth] = useState(960);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const rideKey = useMemo(() => createRideInteractionKey(ride), [ride]);

  const chartModel = useMemo(() => {
    if (!ride || ride.records.length === 0) {
      return createEmptyChartModel(ftpWatts);
    }

    return buildRideAnalysisChartModel(ride, ftpWatts, respiratoryPoints);
  }, [ride, ftpWatts, respiratoryPoints]);

  const { activeViewport, chartModel: safeChartModel, durationSeconds, effectiveSelectedIntervalId, hasRideData, safeDuration, safePlotWidth, xTicks } = useMemo(
    () =>
      buildRideAnalysisChartViewState({
        ride,
        selectedIntervalId,
        plotWidth,
        viewport,
        chartModel
      }),
    [ride, selectedIntervalId, plotWidth, viewport, chartModel]
  );

  useEffect(() => {
    setViewport(null);
    setHoveredElapsedSeconds(null);
    setIsDragging(false);
    setDragStartX(null);
    setDragStartViewport(null);
    setTouchPinch(null);
    onSelectedIntervalChange(null);
  }, [rideKey, onSelectedIntervalChange]);

  useEffect(() => {
    if (!hasRideData) {
      if (viewport !== null) {
        setViewport(null);
      }

      return;
    }

    if (hasInvalidViewport(viewport, safeChartModel.domainStartSeconds, safeChartModel.domainEndSeconds)) {
      setViewport(getFullRideViewport(safeChartModel));
    }
  }, [hasRideData, safeChartModel.domainStartSeconds, safeChartModel.domainEndSeconds, viewport]);

  useEffect(() => {
    if (!selectedIntervalId) {
      return;
    }

    if (!effectiveSelectedIntervalId) {
      onSelectedIntervalChange(null);
      return;
    }

    const focusedViewport = computeViewportForInterval(safeChartModel, effectiveSelectedIntervalId);
    if (!focusedViewport) {
      onSelectedIntervalChange(null);
      return;
    }

    setViewport(clampView(safeChartModel.domainStartSeconds, safeChartModel.domainEndSeconds, focusedViewport));
  }, [selectedIntervalId, effectiveSelectedIntervalId, onSelectedIntervalChange, safeChartModel]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    if (typeof ResizeObserver === "undefined") {
      const width = element.getBoundingClientRect().width;
      setPlotWidth(getSafePlotDimension(width - CHART_PADDING_LEFT - CHART_PADDING_RIGHT));
      return;
    }

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      setPlotWidth(getSafePlotDimension(entry.contentRect.width - CHART_PADDING_LEFT - CHART_PADDING_RIGHT));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const elapsedValues = safeChartModel.points.map(point => point.elapsedSeconds);

  const xScale = (elapsedSeconds: number) => {
    const ratio = (elapsedSeconds - activeViewport.startSeconds) / safeDuration;
    const position = CHART_PADDING_LEFT + ratio * safePlotWidth;
    return Number.isFinite(position) ? position : CHART_PADDING_LEFT;
  };

  const elapsedAtPosition = (clientX: number): number => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) {
      return activeViewport.startSeconds;
    }

    const px = Math.max(CHART_PADDING_LEFT, Math.min(CHART_PADDING_LEFT + safePlotWidth, clientX - rect.left));
    const ratio = (px - CHART_PADDING_LEFT) / Math.max(1, safePlotWidth);
    return activeViewport.startSeconds + ratio * durationSeconds;
  };

  const hoveredPointIndex = hoveredElapsedSeconds === null ? -1 : findNearestPointIndex(safeChartModel.points, hoveredElapsedSeconds);
  const hoveredPoint = hoveredPointIndex >= 0 ? safeChartModel.points[hoveredPointIndex] : null;

  const currentIntervalId = hoveredPoint
    ? findIntervalIdAtElapsed(safeChartModel.overlays, hoveredPoint.elapsedSeconds, true)
    : null;

  const currentIntervalOverlay = currentIntervalId
    ? safeChartModel.overlaysByIntervalId[currentIntervalId] ?? safeChartModel.overlays.find(overlay => overlay.intervalId === currentIntervalId) ?? null
    : null;
  const showParsingState = isParsingRide;
  const showEmptyState = !isParsingRide && !hasRideData;

  function zoomByFactor(centerSeconds: number, factor: number) {
    const currentSpan = Math.max(MIN_WINDOW_SECONDS, activeViewport.endSeconds - activeViewport.startSeconds);
    const nextSpan = Math.max(
      MIN_WINDOW_SECONDS,
      Math.min(safeChartModel.domainEndSeconds - safeChartModel.domainStartSeconds, currentSpan * factor)
    );

    const startSeconds = centerSeconds - ((centerSeconds - activeViewport.startSeconds) / currentSpan) * nextSpan;
    const endSeconds = startSeconds + nextSpan;

    setViewport(clampView(safeChartModel.domainStartSeconds, safeChartModel.domainEndSeconds, { startSeconds, endSeconds }));
  }

  function panBySeconds(deltaSeconds: number) {
    setViewport(
      clampView(safeChartModel.domainStartSeconds, safeChartModel.domainEndSeconds, {
        startSeconds: activeViewport.startSeconds + deltaSeconds,
        endSeconds: activeViewport.endSeconds + deltaSeconds
      })
    );
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    const element = event.currentTarget;
    element.setPointerCapture(event.pointerId);
    setIsDragging(true);
    setDragStartX(event.clientX);
    setDragStartViewport(activeViewport);
    setHoveredElapsedSeconds(elapsedAtPosition(event.clientX));
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const elapsedSeconds = elapsedAtPosition(event.clientX);
    setHoveredElapsedSeconds(elapsedSeconds);

    if (!isDragging || dragStartX === null || !dragStartViewport) {
      return;
    }

    const pxDelta = event.clientX - dragStartX;
    const secondsPerPixel = (dragStartViewport.endSeconds - dragStartViewport.startSeconds) / Math.max(1, safePlotWidth);
    const deltaSeconds = -pxDelta * secondsPerPixel;

    setViewport(
      clampView(safeChartModel.domainStartSeconds, safeChartModel.domainEndSeconds, {
        startSeconds: dragStartViewport.startSeconds + deltaSeconds,
        endSeconds: dragStartViewport.endSeconds + deltaSeconds
      })
    );
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    const elapsedSeconds = elapsedAtPosition(event.clientX);
    if (isDragging && dragStartX !== null && Math.abs(event.clientX - dragStartX) < 6) {
      const clickedIntervalId = findIntervalIdAtElapsed(safeChartModel.overlays, elapsedSeconds, false);
      onSelectedIntervalChange(clickedIntervalId);
    }

    setIsDragging(false);
    setDragStartX(null);
    setDragStartViewport(null);
  }

  function handleWheel(event: ReactWheelEvent<HTMLDivElement>) {
    event.preventDefault();

    const elapsedSeconds = elapsedAtPosition(event.clientX);
    if (event.ctrlKey || Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      const factor = event.deltaY > 0 ? 1.16 : 0.86;
      zoomByFactor(elapsedSeconds, factor);
      return;
    }

    const secondsPerPixel = (activeViewport.endSeconds - activeViewport.startSeconds) / Math.max(1, safePlotWidth);
    panBySeconds(event.deltaX * secondsPerPixel);
  }

  function handleTouchStart(event: ReactTouchEvent<HTMLDivElement>) {
    if (event.touches.length < 2) {
      return;
    }

    const distance = getTouchDistance(event.touches);
    if (!distance) {
      return;
    }

    setTouchPinch({
      initialDistancePx: distance,
      initialViewport: activeViewport
    });
  }

  function handleTouchMove(event: ReactTouchEvent<HTMLDivElement>) {
    if (!touchPinch || event.touches.length < 2) {
      return;
    }

    event.preventDefault();

    const distance = getTouchDistance(event.touches);
    if (!distance || touchPinch.initialDistancePx <= 0) {
      return;
    }

    const centerX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
    const centerSeconds = elapsedAtPosition(centerX);
    const factor = touchPinch.initialDistancePx / distance;

    const span = Math.max(MIN_WINDOW_SECONDS, touchPinch.initialViewport.endSeconds - touchPinch.initialViewport.startSeconds);
    const nextSpan = Math.max(
      MIN_WINDOW_SECONDS,
      Math.min(safeChartModel.domainEndSeconds - safeChartModel.domainStartSeconds, span * factor)
    );

    const relativeCenter = (centerSeconds - touchPinch.initialViewport.startSeconds) / Math.max(1e-9, span);
    const startSeconds = centerSeconds - relativeCenter * nextSpan;
    const endSeconds = startSeconds + nextSpan;

    setViewport(clampView(safeChartModel.domainStartSeconds, safeChartModel.domainEndSeconds, { startSeconds, endSeconds }));
  }

  function handleTouchEnd() {
    setTouchPinch(null);
  }

  const chartRows: Array<{
    id: string;
    title: string;
    unit: string;
    series: Array<{ key: ChartSeriesKey; color: string; strokeWidth: number; visible: boolean }>;
    hasData: boolean;
    yValues: Array<number | null>;
    ftpLine?: number | null;
  }> = [
    {
      id: "power",
      title: "Power",
      unit: "W",
      series: [
        { key: "powerWatts", color: "#f59e0b", strokeWidth: 1, visible: true },
        { key: "power30sWatts", color: "#facc15", strokeWidth: 1.8, visible: showSmoothedPower }
      ],
      hasData: safeChartModel.hasPower,
      yValues: safeChartModel.points.map(point => point.powerWatts),
      ftpLine: safeChartModel.ftpWatts
    },
    {
      id: "heart-rate",
      title: "Heart Rate",
      unit: "bpm",
      series: [{ key: "heartRateBpm", color: "#ef4444", strokeWidth: 1.3, visible: true }],
      hasData: safeChartModel.hasHeartRate,
      yValues: safeChartModel.points.map(point => point.heartRateBpm)
    },
    {
      id: "minute-ventilation",
      title: "Minute Ventilation",
      unit: "L/min",
      series: [{ key: "minuteVentilation", color: "#14b8a6", strokeWidth: 1.3, visible: true }],
      hasData: safeChartModel.hasMinuteVentilation,
      yValues: safeChartModel.points.map(point => point.minuteVentilation)
    },
    {
      id: "breathing-frequency",
      title: "Breathing Frequency",
      unit: "br/min",
      series: [{ key: "breathingFrequency", color: "#06b6d4", strokeWidth: 1.3, visible: true }],
      hasData: safeChartModel.hasBreathingFrequency,
      yValues: safeChartModel.points.map(point => point.breathingFrequency)
    },
    {
      id: "cadence",
      title: "Cadence",
      unit: "rpm",
      series: [{ key: "cadenceRpm", color: "#38bdf8", strokeWidth: 1.3, visible: true }],
      hasData: safeChartModel.hasCadence,
      yValues: safeChartModel.points.map(point => point.cadenceRpm)
    },
    {
      id: "elevation",
      title: "Elevation",
      unit: "ft",
      series: [{ key: "elevationFeet", color: "#22c55e", strokeWidth: 1.2, visible: true }],
      hasData: safeChartModel.hasElevation,
      yValues: safeChartModel.points.map(point => point.elevationFeet)
    }
  ];

  return (
    <section className="panel ride-analysis-panel">
      <div className="ride-summary-header">
        <div>
          <p className="eyebrow">RIDE ANALYSIS</p>
          <h2>Interactive ride visualization</h2>
        </div>
        {!showParsingState && !showEmptyState && (
          <div className="ride-analysis-controls">
            <label className="ride-analysis-toggle">
              <input
                type="checkbox"
                checked={showSmoothedPower}
                onChange={event => setShowSmoothedPower(event.target.checked)}
              />
              <span>30s power</span>
            </label>
            <button
              type="button"
              className="ride-analysis-button"
              onClick={() => {
                setViewport(getFullRideViewport(safeChartModel));
                setHoveredElapsedSeconds(null);
              }}
            >
              Show Full Ride
            </button>
          </div>
        )}
      </div>

      {showParsingState ? (
        <p className="summary-copy">Parsing ride records and preparing chart data...</p>
      ) : showEmptyState ? (
        <p className="summary-copy">
          {rideParseError
            ? "No parsed ride is available. Upload a valid FIT file to view synchronized charts."
            : "Upload a FIT file to view power, heart rate, cadence, and elevation over elapsed ride time."}
        </p>
      ) : (
        <>
          <div className="ride-analysis-legend">
            <span className="legend-item power-raw">Power</span>
            {showSmoothedPower && <span className="legend-item power-smooth">30s power</span>}
            <span className="legend-item interval-primary">Primary intervals</span>
            <span className="legend-item interval-supporting">Supporting intervals</span>
            <span className="legend-item interval-recovery">Recoveries</span>
          </div>

          <div
            className="ride-analysis-chart-stack"
            ref={containerRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onPointerLeave={() => {
              if (!isDragging) {
                setHoveredElapsedSeconds(null);
              }
            }}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {chartRows.filter(row => row.hasData).map(row => {
              const visibleValues = safeChartModel.points.map(point => {
                if (point.elapsedSeconds < activeViewport.startSeconds || point.elapsedSeconds > activeViewport.endSeconds) {
                  return null;
                }

                return point[row.series[0].key as keyof typeof point] as number | null;
              });

              const range = computeValueRange(visibleValues);
              const chartInnerHeight = CHART_HEIGHT - CHART_PADDING_TOP - CHART_PADDING_BOTTOM;

              const yScale = (value: number) => {
                if (!range) {
                  return CHART_PADDING_TOP + chartInnerHeight / 2;
                }

                const ratio = (value - range.min) / Math.max(1e-9, range.max - range.min);
                return CHART_PADDING_TOP + (1 - ratio) * chartInnerHeight;
              };

              const hoveredX = hoveredPoint ? xScale(hoveredPoint.elapsedSeconds) : null;
              const selectedOverlay = effectiveSelectedIntervalId ? safeChartModel.overlaysByIntervalId[effectiveSelectedIntervalId] : null;

              return (
                <div className="ride-analysis-chart-row" key={row.id}>
                  <div className="ride-analysis-chart-header">
                    <strong>{row.title}</strong>
                    {range ? <span>{`${range.min.toFixed(0)}-${range.max.toFixed(0)} ${row.unit}`}</span> : <span>No data in this stream</span>}
                  </div>

                  <svg width="100%" height={CHART_HEIGHT} viewBox={`0 0 ${safePlotWidth + CHART_PADDING_LEFT + CHART_PADDING_RIGHT} ${CHART_HEIGHT}`}>
                    <rect
                      x={CHART_PADDING_LEFT}
                      y={CHART_PADDING_TOP}
                      width={safePlotWidth}
                      height={chartInnerHeight}
                      className="ride-analysis-plot-bg"
                    />

                    {safeChartModel.overlays.map(overlay => {
                      const startX = xScale(Math.max(activeViewport.startSeconds, overlay.startElapsedSeconds));
                      const endX = xScale(Math.min(activeViewport.endSeconds, overlay.endElapsedSeconds));
                      if (!Number.isFinite(startX) || !Number.isFinite(endX)) {
                        return null;
                      }

                      const width = endX - startX;

                      if (width <= 0) {
                        return null;
                      }

                      return (
                        <rect
                          key={`${row.id}-${overlay.id}`}
                          x={startX}
                          y={CHART_PADDING_TOP}
                          width={width}
                          height={chartInnerHeight}
                          className={`interval-overlay ${overlay.kind} ${effectiveSelectedIntervalId && overlay.intervalId === effectiveSelectedIntervalId ? "is-selected" : ""}`}
                        />
                      );
                    })}

                    {selectedOverlay && (
                      <rect
                        x={xScale(Math.max(activeViewport.startSeconds, selectedOverlay.startElapsedSeconds))}
                        y={CHART_PADDING_TOP}
                        width={Math.max(0, xScale(Math.min(activeViewport.endSeconds, selectedOverlay.endElapsedSeconds)) - xScale(Math.max(activeViewport.startSeconds, selectedOverlay.startElapsedSeconds)))}
                        height={chartInnerHeight}
                        className="interval-focus-outline"
                      />
                    )}

                    {row.series.map(series => {
                      if (!series.visible || !range) {
                        return null;
                      }

                      const yValues = safeChartModel.points.map(point => {
                        if (point.elapsedSeconds < activeViewport.startSeconds || point.elapsedSeconds > activeViewport.endSeconds) {
                          return null;
                        }

                        return point[series.key];
                      });

                      const path = buildLinePath(elapsedValues, yValues, xScale, yScale, MAX_RENDER_POINTS);
                      if (!path) {
                        return null;
                      }

                      return (
                        <path
                          key={`${row.id}-${series.key}`}
                          d={path}
                          fill="none"
                          stroke={series.color}
                          strokeWidth={series.strokeWidth}
                          vectorEffect="non-scaling-stroke"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                      );
                    })}

                    {toFiniteNumber(row.ftpLine) !== null && range && row.id === "power" && (
                      <line
                        x1={CHART_PADDING_LEFT}
                        x2={CHART_PADDING_LEFT + safePlotWidth}
                        y1={yScale(row.ftpLine as number)}
                        y2={yScale(row.ftpLine as number)}
                        className="ftp-reference-line"
                      />
                    )}

                    {hoveredX !== null && (
                      <line
                        x1={hoveredX}
                        x2={hoveredX}
                        y1={CHART_PADDING_TOP}
                        y2={CHART_PADDING_TOP + chartInnerHeight}
                        className="hover-scrubber-line"
                      />
                    )}

                    {xTicks.map((tick, index) => (
                      <g key={`${row.id}-tick-${index}`}>
                        <line
                          x1={xScale(tick)}
                          x2={xScale(tick)}
                          y1={CHART_HEIGHT - CHART_PADDING_BOTTOM + 2}
                          y2={CHART_HEIGHT - CHART_PADDING_BOTTOM + 6}
                          className="x-axis-tick"
                        />
                        {row.id === "elevation" && (
                          <text x={xScale(tick)} y={CHART_HEIGHT - 6} textAnchor="middle" className="x-axis-label">
                            {formatElapsedTime(tick)}
                          </text>
                        )}
                      </g>
                    ))}
                  </svg>
                </div>
              );
            })}

            {hoveredPoint && (
              <div
                className="ride-analysis-tooltip"
                style={{
                  left: `${Math.min(safePlotWidth + CHART_PADDING_LEFT - 8, Math.max(CHART_PADDING_LEFT + 8, xScale(hoveredPoint.elapsedSeconds)))}px`
                }}
              >
                <div><strong>{formatElapsedTime(hoveredPoint.elapsedSeconds)}</strong></div>
                <div>Clock {formatClockTime(hoveredPoint.clockTime)}</div>
                <div>Power {formatMetric(hoveredPoint.powerWatts, "W")}</div>
                <div>30s {formatMetric(hoveredPoint.power30sWatts, "W")}</div>
                <div>HR {formatMetric(hoveredPoint.heartRateBpm, "bpm")}</div>
                <div>Cadence {formatMetric(hoveredPoint.cadenceRpm, "rpm")}</div>
                <div>Elevation {formatMetric(hoveredPoint.elevationFeet, "ft")}</div>
                <div>Speed {formatMetric(hoveredPoint.speedMph, "mph", 1)}</div>
                <div>Distance {formatMetric(hoveredPoint.distanceMiles, "mi", 2)}</div>
                <div>Interval {currentIntervalOverlay ? formatClassification(currentIntervalOverlay.classification) : "--"}</div>
              </div>
            )}
          </div>

          {!safeChartModel.hasPower && <p className="chart-stream-warning">Power data unavailable for this ride.</p>}
          {!safeChartModel.hasHeartRate && <p className="chart-stream-warning">Heart rate data unavailable for this ride.</p>}
          {!safeChartModel.hasCadence && <p className="chart-stream-warning">Cadence data unavailable for this ride.</p>}
          {!safeChartModel.hasElevation && <p className="chart-stream-warning">Elevation data unavailable for this ride.</p>}
          {!safeChartModel.hasGps && <p className="chart-stream-warning">GPS-derived speed/distance unavailable for tooltip context.</p>}
        </>
      )}
    </section>
  );
}
